import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Home as HomeIcon, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PageHead } from "../components/site/SiteLayout";
import { useLang, useTitle } from "../lib/i18n";
import { useConfig } from "../lib/config";
import { api, errMsg, money, imgUrl } from "../lib/api";
import { ServiceCard } from "./Home";

export function About() {
  const { t } = useLang();
  useTitle(t.about.title);
  const icons = [Heart, HomeIcon, MessageCircle];
  return (
    <div>
      <PageHead eyebrow="Hialeah, FL" title={t.about.title} sub={t.about.p1} />
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 grid gap-12 lg:grid-cols-2 items-center">
        <img src="/images/detail.jpg" alt="Mainliz cleaning team at work" className="rounded-3xl h-[380px] w-full object-cover" />
        <div className="space-y-6">
          <p className="text-lg text-slate-700">{t.about.p2}</p>
          <h2 className="text-2xl font-semibold text-slate-900">{t.about.valuesTitle}</h2>
          {t.about.values.map(([h, p], i) => {
            const I = icons[i];
            return <div key={h} className="flex gap-4"><I className="text-sky-600 shrink-0" /><div><h3 className="font-semibold">{h}</h3><p className="text-sm text-slate-600">{p}</p></div></div>;
          })}
          <Link to="/book" className="btn-primary" data-testid="about-book-cta">{t.hero.cta}</Link>
        </div>
      </section>
    </div>
  );
}

export function Services() {
  const { t } = useLang();
  const cfg = useConfig();
  useTitle(t.services.title);
  return (
    <div>
      <PageHead eyebrow={t.hero.eyebrow} title={t.services.title} sub={t.services.sub} />
      {["residential", "commercial"].map((cat) => (
        <section key={cat} className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">{t.services[cat]}</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(cfg?.services || []).filter((s) => s.category === cat).map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

const Row = ({ k, v, testid }) => <div className="flex justify-between py-2.5 border-b border-slate-100 last:border-0 text-sm"><span className="text-slate-600">{k}</span><span className="font-semibold text-slate-900" data-testid={testid}>{v}</span></div>;

export function Pricing() {
  const { t, lang } = useLang();
  const cfg = useConfig();
  useTitle(t.pricing.title);
  const p = cfg?.pricing || {};
  const fd = p.frequency_discounts || {};
  return (
    <div>
      <PageHead eyebrow={t.hero.eyebrow} title={t.pricing.title} sub={t.pricing.sub} />
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid gap-6 lg:grid-cols-3">
        <div className="card lg:col-span-2" data-testid="pricing-services-table">
          <h2 className="text-xl font-semibold mb-4">{t.pricing.base}</h2>
          {(cfg?.services || []).map((s) => <Row key={s.id} k={lang === "es" ? s.name_es : s.name_en} v={s.pricing_model === "quote" ? t.services.quoteOnly : `${t.services.from} ${money(Math.max(s.base_price, s.min_price))}`} />)}
        </div>
        <div className="space-y-6">
          <div className="card">
            <Row k={t.pricing.bedroom} v={money(p.bedroom_rate)} testid="pricing-bedroom-rate" />
            <Row k={t.pricing.bathroom} v={money(p.bathroom_rate)} />
            <Row k={`${t.pricing.sqft} ${p.included_sqft || 0}`} v={`$${p.sqft_rate || 0}`} />
            <Row k={t.pricing.minimum} v={money(p.min_price)} />
            {p.weekend_surcharge_percent > 0 && <Row k={t.pricing.weekend} v={`${p.weekend_surcharge_percent}%`} />}
            <Row k={t.pricing.deposit} v={`${p.deposit_percent}%`} />
          </div>
          <div className="card bg-slate-900 text-white border-slate-900">
            <h3 className="font-semibold mb-3">{t.pricing.freq}</h3>
            {["weekly", "biweekly", "monthly"].map((f) => <div key={f} className="flex justify-between py-1.5 text-sm"><span className="text-slate-300">{t.pricing[f]}</span><span className="font-semibold text-sky-300">-{fd[f] || 0}%</span></div>)}
          </div>
        </div>
        <div className="card lg:col-span-2">
          <h2 className="text-xl font-semibold mb-4">{t.pricing.addons}</h2>
          <div className="grid sm:grid-cols-2 gap-x-8">
            {(cfg?.addons || []).map((a) => (
              <div key={a.id} className="py-2.5 border-b border-slate-100">
                <div className="flex justify-between items-center gap-3 text-sm"><span className="flex items-center gap-3 text-slate-700 font-medium">{a.image_url && <img src={imgUrl(a.image_url)} alt="" className="h-10 w-10 rounded-lg object-cover" />}{lang === "es" ? a.name_es : a.name_en}</span><span className="font-semibold text-slate-900">{`${money(a.price)}${a.price_type === "per_unit" ? ` ${t.pricing.perUnit}` : ""}`}</span></div>
                {(a.desc_en || a.desc_es) && <p className="text-xs text-slate-500 mt-1">{lang === "es" ? a.desc_es || a.desc_en : a.desc_en || a.desc_es}</p>}
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <h3 className="font-semibold mb-2">{t.pricing.travel}</h3>
          <p className="text-sm text-slate-600">{t.pricing.travelNote}</p>
          <Link to="/book" className="btn-primary w-full mt-5" data-testid="pricing-calc-cta">{t.pricing.calc}</Link>
        </div>
      </section>
    </div>
  );
}

export function FAQ() {
  const { t } = useLang();
  useTitle(t.faq.title);
  return (
    <div>
      <PageHead title={t.faq.title} />
      <section className="max-w-3xl mx-auto px-4 lg:px-8 py-14">
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {t.faq.items.map(([q, a], i) => (
            <AccordionItem key={q} value={`i${i}`}>
              <AccordionTrigger className="text-left text-base font-semibold" data-testid={`faq-item-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}

export function Contact() {
  const { t } = useLang();
  const cfg = useConfig();
  useTitle(t.contact.title);
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      toast.success(t.contact.sent);
      setF({ name: "", email: "", phone: "", message: "" });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const b = cfg?.business || {};
  return (
    <div>
      <PageHead title={t.contact.title} sub={t.contact.sub} />
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid gap-10 lg:grid-cols-3">
        <form onSubmit={submit} className="card lg:col-span-2 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="label">{t.book.name}</label><input required className="field" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name-input" /></div>
            <div><label className="label">{t.book.email}</label><input required type="email" className="field" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email-input" /></div>
          </div>
          <div><label className="label">{t.book.phone}</label><input className="field" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="contact-phone-input" /></div>
          <div><label className="label">{t.contact.message}</label><textarea required rows={5} className="field" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message-input" /></div>
          <button className="btn-primary" disabled={busy} data-testid="contact-submit-button">{t.contact.send}</button>
        </form>
        <div className="card space-y-3 h-fit">
          <h3 className="font-semibold">Mainliz Cleaning Services</h3>
          <p className="text-sm text-slate-600">{b.address}</p>
          <p className="text-sm text-slate-600">{b.phone}</p>
          <p className="text-sm text-slate-600">{b.email}</p>
          <p className="text-sm text-slate-600">{b.hours}</p>
          <p className="text-sm text-slate-600">{b.service_area}</p>
        </div>
      </section>
    </div>
  );
}

export function MyBookings() {
  const { t, lang } = useLang();
  useTitle(t.my.title);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/customers/lookup", { email, lang }); toast.success(t.my.sent); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHead title={t.my.title} sub={t.my.sub} />
      <form onSubmit={submit} className="max-w-xl mx-auto px-4 py-14 flex flex-col sm:flex-row gap-3" data-testid="my-bookings-form">
        <input required type="email" className="field" placeholder={t.book.email} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="my-bookings-email-input" />
        <button className="btn-primary shrink-0" disabled={busy} data-testid="my-bookings-submit">{t.my.send}</button>
      </form>
    </div>
  );
}
